<script setup lang="ts">
import CoreIcon from '@core/core-icon.vue';
import CoreLoader from '@core/core-loader.vue';
import Popup from '@/components/popup/popup.vue';
import { onMounted, ref, computed } from 'vue';
import { useSocialProviders } from '@/composables/socialProviders';
import { useUserStore } from '@/store/user';
import { useUserApi } from '@/store/api/userApi';
import { useToast } from 'vue-toast-notification';
import { SocialProvider, UserConnectNotification } from '@/store/api/interfaces';
import isInTelegram from "@/utils/isInTelegram";
import { ApiError } from '@/composables/useApi';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { onBeforeUnmount } from 'vue';

const { t } = useI18n();
const toast = useToast();
const isMiniApp = isInTelegram();
const userStore = useUserStore();
const userApi = useUserApi();
const route = useRoute();
const emit = defineEmits([ 'connected' ]);
const socialProviders = useSocialProviders();
const capitalize = (s: string) => s ? s[0].toUpperCase() + s.slice(1) : '';
const connectPopup = ref();
const connectNotification = ref<UserConnectNotification>({});

interface ProviderConfig {
	key: SocialProvider | 'apple';
	name: string;
	icon: string;
	enabled: boolean;
	bonus?: string;
	subtitle?: string;
	connectTitle?: string;
}

const providers = computed<ProviderConfig[]>(() => [
	{
		key: 'google',
		name: 'Google',
		icon: '/images/google.svg',
		enabled: true,
		subtitle: t('accountIndex.connect_google_subtitle'),
	},
	{
		key: 'telegram',
		name: 'Telegram',
		icon: '/images/telegram.svg',
		enabled: !isMiniApp,
		subtitle: t('accountIndex.connect_telegram_subtitle'),
	},
	{
		key: 'apple',
		name: 'Apple',
		icon: '/images/apple.svg',
		enabled: false,
		subtitle: t('accountIndex.connect_apple_subtitle'),
	},
]);

const isConnected = (provider: ProviderConfig): boolean => {
	return !!userStore.user?.[provider.key as keyof typeof userStore.user];
};

const connectedProviders = computed(() =>
	providers.value.filter(p => p.enabled && isConnected(p))
);

const authMethodsCount = computed(() => {
	let count = 0;
	if (userStore.user?.telegram) count++;
	if (userStore.user?.google) count++;
	if (userStore.user?.email_verified_at) count++;
	return count;
});

const canDisconnect = computed(() => authMethodsCount.value > 1);

const unconnectedProviders = computed(() =>
	providers.value.filter(p => p.enabled && !isConnected(p))
);

const connectPollInterval = ref<ReturnType<typeof setInterval> | null>(null);
const connectPollTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

const stopConnectPolling = () => {
	if (connectPollInterval.value) {
		clearInterval(connectPollInterval.value);
		connectPollInterval.value = null;
	}
	if (connectPollTimeout.value) {
		clearTimeout(connectPollTimeout.value);
		connectPollTimeout.value = null;
	}
};

const connectProviderHandler = async (provider: ProviderConfig) => {
	if (isConnected(provider)) return;

	connectNotification.value = {
		provider: provider.key as SocialProvider,
		status: 'waiting',
	};
	connectPopup.value.open();

	try {
		await socialProviders.connect(provider.key as SocialProvider);

		// In Mini App: poll user profile to detect when provider is connected
		if (isMiniApp) {
			stopConnectPolling();
			connectPollInterval.value = setInterval(async () => {
				await userStore.methods.set();
				if (isConnected(provider)) {
					stopConnectPolling();
					connectNotification.value.status = 'success';
					connectPopup.value.close();
					emit('connected', { provider: provider.key, status: 'success' });
				}
			}, 3000);

			// Stop polling after 2 minutes
			connectPollTimeout.value = setTimeout(() => stopConnectPolling(), 120_000);
		}
	} catch (error) {
		connectNotification.value.status = 'failed';
		if (error instanceof ApiError) {
			connectNotification.value.code = error.code;
		}
		connectPopup.value.open();
	}
};

const disconnectLoading = ref(false);
const disconnectPopup = ref();
const disconnectTarget = ref<ProviderConfig | null>(null);

const showDisconnectConfirm = (provider: ProviderConfig) => {
	disconnectTarget.value = provider;
	disconnectPopup.value.open();
};

const onDisconnectConfirm = async () => {
	if (!disconnectTarget.value) return;
	disconnectPopup.value.close();
	await executeDisconnect(disconnectTarget.value);
};

const onDisconnectCancel = () => {
	disconnectPopup.value.close();
	disconnectTarget.value = null;
};

const executeDisconnect = async (provider: ProviderConfig) => {
	if (disconnectLoading.value) return;

	try {
		disconnectLoading.value = true;
		await userApi.disconnect(provider.key as SocialProvider);
		await userStore.methods.set();
	} catch (error) {
		console.error('DisconnectError', error);
		if (error instanceof ApiError) {
			toast.error(t(`errorCode.${error.code}`), {
				position: 'top'
			});
		}
	} finally {
		disconnectLoading.value = false;
	}
};

const onConnectMessage = async (event: MessageEvent) => {
	if (event.origin !== window.location.origin) return;
	if (event.data?.type !== 'connect-result') return;

	const notification = event.data.notification as UserConnectNotification;
	connectNotification.value = notification;

	if (notification.status === 'success') {
		await userStore.methods.set();
		connectPopup.value.close();
		emit('connected', notification);
	}
};

onMounted(() => {
	window.addEventListener('message', onConnectMessage);

	const { notification } = route.query;
	if (typeof notification === 'string' && notification) {
		const parsed = JSON.parse(notification) as UserConnectNotification;
		connectNotification.value = parsed;
		connectPopup.value.open();
	}
});

onBeforeUnmount(() => {
	window.removeEventListener('message', onConnectMessage);
	stopConnectPolling();
});
</script>

<template>
	<div v-bind="$attrs" class="part-social-connect">
		<!-- Connected providers: each as separate card -->
		<div
			v-for="provider in connectedProviders"
			:key="provider.key"
			class="part-social-connect__card"
		>
			<div class="part-social-connect__provider">
				<div class="part-social-connect__icon-wrap part-social-connect__icon-wrap--white">
					<img :src="provider.icon" class="part-social-connect__icon"/>
				</div>
				<div class="part-social-connect__info">
					<span class="part-social-connect__name-group">
						<span class="part-social-connect__name">{{ provider.name }}</span>
						<span class="part-social-connect__badge-done">
							<core-icon name="checkmark" viewBox="0 0 15 14" />
						</span>
					</span>
					<span class="part-social-connect__subtitle">
						{{ t('accountIndex.connected_subtitle') }}
					</span>
				</div>
			</div>
			<!-- Отвязка — иконкой без подложки: действие редкое и подтверждается
				 в попапе, а кнопка с текстом спорила бы с содержимым строки -->
			<button
				v-if="canDisconnect"
				type="button"
				class="part-social-connect__action part-social-connect__action--disconnect"
				:disabled="disconnectLoading"
				:aria-label="t('accountIndex.disconnect_button')"
				:title="t('accountIndex.disconnect_button')"
				@click="showDisconnectConfirm(provider)"
			>
				<core-icon
					name="unlink"
					viewBox="0 0 16 16"
					class="part-social-connect__action-icon part-social-connect__action-icon--always"
				/>
			</button>
		</div>

		<!-- Unconnected providers: grouped in one card -->
		<div
			v-if="unconnectedProviders.length"
			class="part-social-connect__card part-social-connect__card--grouped"
		>
			<div
				v-for="provider in unconnectedProviders"
				:key="provider.key"
				class="part-social-connect__row"
			>
				<div class="part-social-connect__provider">
					<div class="part-social-connect__icon-wrap">
						<img :src="provider.icon" class="part-social-connect__icon" />
						<span v-if="provider.bonus" class="part-social-connect__badge-bonus">
							<core-icon name="sale" viewBox="0 0 18 18" />
						</span>
					</div>
					<div class="part-social-connect__info">
						<span class="part-social-connect__name">
							{{ provider.connectTitle || provider.name }}
						</span>
						<span v-if="provider.bonus" class="part-social-connect__bonus">
							{{ provider.bonus }}
						</span>
						<span v-else-if="provider.subtitle" class="part-social-connect__subtitle">
							{{ provider.subtitle }}
						</span>
					</div>
				</div>
				<button
					class="part-social-connect__action part-social-connect__action--connect"
					@click="connectProviderHandler(provider)"
				>
					<core-icon
						name="link"
						viewBox="0 0 16 16"
						class="part-social-connect__action-icon"
					/>
					<span class="part-social-connect__action-text">
						{{ t('accountIndex.connect_button') }}
					</span>
				</button>
			</div>
		</div>
	</div>

	<popup ref="disconnectPopup">
		<img
			v-if="disconnectTarget?.icon"
			:src="disconnectTarget.icon"
			alt=""
			class="popup__icon"
		/>
		<div class="popup__text">
			<h3 class="popup__title">{{ t('accountIndex.disconnect_confirm_title') }}</h3>
			<p class="popup__subtitle">{{ t('accountIndex.disconnect_confirm_text') }}</p>
		</div>
		<div class="popup__buttons">
			<button class="popup__btn popup__btn--primary" @click="onDisconnectConfirm">
				{{ t('accountIndex.disconnect_button') }}
			</button>
			<button class="popup__btn popup__btn--secondary" @click="onDisconnectCancel">
				{{ t('common.buttons.cancel') }}
			</button>
		</div>
	</popup>

	<popup ref="connectPopup">
		<!-- Waiting -->
		<template v-if="connectNotification.status === 'waiting'">
			<core-loader class="popup__loader" />
			<div class="popup__text">
				<p class="popup__subtitle">
					{{ t('accountIndex.notification.waiting', { provider: capitalize(connectNotification.provider || '') }) }}
				</p>
			</div>
		</template>

		<!-- Success -->
		<template v-else-if="connectNotification.status === 'success'">
			<div class="popup__status-icon popup__status-icon--success">
				<core-icon name="checkmark" viewBox="0 0 15 14" />
			</div>
			<div class="popup__text">
				<p class="popup__subtitle">
					{{ t('accountIndex.notification.success', { provider: capitalize(connectNotification.provider || '') }) }}
				</p>
			</div>
		</template>

		<!-- Error -->
		<template v-else>
			<div class="popup__status-icon popup__status-icon--error">
				<core-icon name="cross" viewBox="0 0 18 18" />
			</div>
			<div class="popup__text">
				<p class="popup__subtitle">
					{{ connectNotification.code
						? t(`errorCode.${connectNotification.code}`, { provider: capitalize(connectNotification.provider || '') })
						: t('accountIndex.notification.failed', { provider: capitalize(connectNotification.provider || '') })
					}}
				</p>
			</div>
		</template>
	</popup>
</template>

<style lang="scss">
.part-social-connect {
	display: flex;
	flex-direction: column;
	gap: 0.8rem;

	// Высота и круг под иконку — как у пунктов меню ниже: карточки аккаунтов
	// и меню стоят в одном столбце и не должны отличаться пластикой
	&__card {
		display: flex;
		align-items: center;
		justify-content: space-between;
		min-height: 5.2rem;
		padding: 0.5rem 1rem 0.5rem 1.2rem;
		border-radius: 1.6rem;
		background-color: var(--color-panel);
		border: 1px solid rgba(var(--color-active-rgb), 0.12);

		@include min-desktop {
			min-height: 6.2rem;
			padding: 0.8rem 1.4rem 0.8rem 1.6rem;
		}

		&--grouped {
			flex-direction: column;
			gap: 0.6rem;
			padding: 0.6rem;

			@include min-desktop {
				padding: 0.8rem;
			}
		}
	}

	&__row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		min-height: 4.2rem;
		padding: 0.3rem 0.4rem 0.3rem 0.6rem;
		border-radius: 1.2rem;
		background-color: var(--color-white);

		@include min-desktop {
			min-height: 4.8rem;
			padding: 0.4rem 0.6rem 0.4rem 0.8rem;
		}

		@include theme-dark {
			background-color: rgba(var(--color-active-rgb), 0.06);
		}
	}

	&__provider {
		display: flex;
		align-items: center;
		gap: 1.2rem;

		@include min-desktop {
			gap: 1.6rem;
		}
	}

	&__icon-wrap {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 3.2rem;
		height: 3.2rem;
		border-radius: 50%;
		background-color: #f7f5fc;
		flex-shrink: 0;

		@include theme-dark {
			background-color: rgba(var(--color-active-rgb), 0.1);
		}

		&--white {
			background-color: var(--color-white);

			@include theme-dark {
				background-color: rgba(var(--color-active-rgb), 0.06);
			}
		}
	}

	&__icon {
		width: 1.8rem;
		height: 1.8rem;
	}

	&__info {
		display: flex;
		flex-direction: column;
		gap: 0.2rem;
	}

	&__name-group {
		display: flex;
		align-items: center;
		gap: 0.6rem;
	}

	&__name {
		font-size: 1.4rem;
		font-weight: 500;

		@include min-desktop {
			@include font-18-pro-medium;
		}
	}

	&__bonus {
		font-size: 1.4rem;
		color: var(--color-purple);
		opacity: 0.7;
		line-height: 1.2;
	}

	&__subtitle {
		@include font-12-pro-regular;
		color: var(--color-text);
		opacity: 0.7;
		line-height: 1.2;
	}

	&__badge-done {
		width: 1.6rem;
		height: 1.6rem;
		border-radius: 50%;
		background-color: var(--color-purple);
		fill: var(--color-white);
		display: flex;
		align-items: center;
		justify-content: center;

		svg {
			width: 1rem;
		}
	}

	&__badge-bonus {
		position: absolute;
		left: -0.4rem;
		bottom: -0.4rem;
		width: 2rem;
		height: 2rem;
		border-radius: 50%;
		background-color: var(--color-purple);
		fill: var(--color-white);
		display: flex;
		align-items: center;
		justify-content: center;

		svg {
			width: 1.2rem;
		}
	}

	&__action {
		padding: 1.2rem 1.6rem;
		border-radius: 1.6rem;
		background-color: var(--color-white);
		color: var(--color-text);
		font-size: 1.4rem;
		cursor: pointer;
		transition: background-color ease 0.2s;
		white-space: nowrap;
		text-align: center;
		border: none;
		height: 4.2rem;
		display: flex;
		align-items: center;
		justify-content: center;

		@include min-desktop {
			@include font-16-pro-regular;
			min-width: 12rem;
		}

		@include theme-dark {
			background-color: rgba(var(--color-active-rgb), 0.06);
		}

		&:hover:not(:disabled) {
			background-color: #f0edf7;

			@include theme-dark {
				background-color: rgba(var(--color-active-rgb), 0.12);
			}
		}

		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}

		&--connect {
			background-color: #f7f5fc;
			min-width: auto;
			width: 4.2rem;
			padding: 0;

			@include min-desktop {
				width: auto;
				min-width: 12rem;
				padding: 1.2rem 1.6rem;
			}

			@include theme-dark {
				background-color: rgba(var(--color-active-rgb), 0.1);
			}
		}

		&--disconnect {
			width: 4.2rem;
			min-width: auto;
			padding: 0;
			background-color: transparent;
			opacity: 0.7;
			transition: opacity 0.2s ease, background-color 0.2s ease;

			@include min-desktop {
				width: 4.2rem;
				min-width: auto;
			}

			@include theme-dark {
				background-color: transparent;
			}

			&:hover:not(:disabled) {
				opacity: 1;
				background-color: rgba(var(--color-active-rgb), 0.08);

				@include theme-dark {
					background-color: rgba(var(--color-active-rgb), 0.12);
				}
			}
		}

		&-icon {
			width: 1.6rem;
			height: 1.6rem;
			stroke: var(--color-text);
			fill: none;

			@include min-desktop {
				display: none;
			}

			// У иконки-кнопки текста нет ни на каком экране; цвет — как у
			// «Копировать» в строке ID: это одинаковые второстепенные действия
			&--always {
				width: 1.6rem;
				height: 1.6rem;
				stroke: var(--color-active);

				@include min-desktop {
					display: block;
				}
			}
		}

		&-text {
			display: none;

			@include min-desktop {
				display: inline;
			}
		}
	}
}
</style>
